import React from 'react';
import { ArrowLeft, Edit, Printer, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { SavedAVV, avvTemplateData, generateAVVTitle, getIncompleteAVVFields, weisungsprotokollColumns, replaceMainContractPlaceholder } from '@/lib/constants/avvTemplateData';
import { renderClauseContent } from './renderClauseContent';

interface AVVPreviewProps {
  avv: SavedAVV;
  onBack: () => void;
  onEdit: () => void;
  onPrint: () => void;
  onFinalize: () => void;
}

const AVVPreview: React.FC<AVVPreviewProps> = ({ avv, onBack, onEdit, onPrint, onFinalize }) => {
  const { clauses, subProcessors, toms, annexes, processor } = avvTemplateData;
  const incompleteFields = getIncompleteAVVFields(avv);
  const isIncomplete = incompleteFields.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Zurück
          </Button>
          <Badge variant={avv.status === 'finalized' ? 'default' : 'secondary'}>
            {avv.status === 'finalized' ? 'Finalisiert' : 'Entwurf'}
          </Badge>
        </div>
        <div className="flex gap-2">
          {avv.status !== 'finalized' && (
            <>
              <Button variant="outline" size="sm" onClick={onEdit}>
                <Edit className="h-4 w-4 mr-1" />
                Bearbeiten
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onFinalize}
                disabled={isIncomplete}
                title={isIncomplete ? 'Alle Pflichtfelder müssen ausgefüllt sein' : undefined}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Finalisieren
              </Button>
            </>
          )}
          <Button size="sm" onClick={onPrint}>
            <Printer className="h-4 w-4 mr-1" />
            Druckansicht / PDF
          </Button>
        </div>
      </div>

      {isIncomplete && avv.status !== 'finalized' && (
        <Alert variant="destructive" className="border-yellow-500 bg-yellow-50 text-yellow-900 dark:bg-yellow-950 dark:text-yellow-200 dark:border-yellow-700 [&>svg]:text-yellow-600">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Unvollständiger Entwurf</AlertTitle>
          <AlertDescription>
            Fehlende Angaben: {incompleteFields.join(', ')}.
            Der AVV kann erst finalisiert werden, wenn alle Angaben vollständig sind.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="py-8 px-6 md:px-12 max-w-4xl mx-auto space-y-8">
          {/* Title */}
          <div className="text-center space-y-4">
            <h1 className="text-2xl font-bold">Auftragsverarbeitungsvertrag</h1>
            <p className="text-lg">gemäß Art. 28 DSGVO</p>
            <p className="text-muted-foreground">{generateAVVTitle(avv.controller.name)}</p>
          </div>

          <Separator />

          {/* Parties */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold mb-2">Auftraggeber (Verantwortlicher)</h3>
              <div className="text-sm space-y-1">
                <p className="font-medium">{avv.controller.name}</p>
                <p>{avv.controller.address}</p>
                {avv.controller.representedBy && <p>Vertreten durch: {avv.controller.representedBy}</p>}
                {avv.controller.dataProtectionOfficer && <p>DSB: {avv.controller.dataProtectionOfficer}</p>}
                {avv.controller.dpoEmail && <p>E-Mail DSB: {avv.controller.dpoEmail}</p>}
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Auftragnehmer (Auftragsverarbeiter)</h3>
              <div className="text-sm space-y-1">
                <p className="font-medium">{processor.name}</p>
                <p>{processor.address}</p>
                <p>Vertreten durch: {processor.representedBy}</p>
                <p>DSB: {processor.dataProtectionOfficer}</p>
                <p>E-Mail DSB: {processor.dpoEmail}</p>
              </div>
            </div>
          </div>

          {/* Project Details */}
          <div>
            <h3 className="font-semibold mb-2">Projektgegenstand</h3>
            <div className="text-sm space-y-1">
              <p><strong>Projekt:</strong> {avv.project.projectName}</p>
              <p>{avv.project.projectDescription}</p>
              {avv.project.contractStart && <p><strong>Laufzeit:</strong> {avv.project.contractStart} bis {avv.project.contractEnd || 'unbefristet'}</p>}
            </div>
          </div>

          <Separator />

          {/* Clauses */}
          {clauses.map(clause => {
            const content = replaceMainContractPlaceholder(clause.content, avv.project.mainContractReference);
            return (
              <div key={clause.id} className="space-y-2">
                <h2 className="text-lg font-bold">{clause.title}</h2>
                <p className="text-xs text-muted-foreground">Rechtsgrundlage: {clause.legalBasis}</p>
                <div className="text-sm">{renderClauseContent(content)}</div>
                <Separator className="mt-4" />
              </div>
            );
          })}

          {/* Annex 1: TOMs */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold">{annexes[0].title}</h2>
            <p className="text-sm">{annexes[0].content}</p>
            {toms.map((tom, i) => (
              <div key={i} className="space-y-2">
                <h4 className="font-semibold text-sm">{tom.title}</h4>
                <div className="text-sm space-y-1">
                  {tom.measures.map((m, j) => (
                    <div key={j} className="flex gap-2 pl-4">
                      <span className="shrink-0">•</span>
                      <span>{m}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <Separator />

          {/* Annex 2: Sub-Processors */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold">{annexes[1].title}</h2>
            <p className="text-sm">{annexes[1].content}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 font-semibold">Unterauftragnehmer</th>
                    <th className="text-left py-2 pr-4 font-semibold">Funktion</th>
                    <th className="text-left py-2 pr-4 font-semibold">Standort</th>
                    <th className="text-left py-2 pr-4 font-semibold">Rechtsgrundlage</th>
                    <th className="text-left py-2 font-semibold">Daten</th>
                  </tr>
                </thead>
                <tbody>
                  {subProcessors.map((sp, i) => (
                    <tr key={i} className="border-b">
                      <td className="py-2 pr-4">{sp.name}</td>
                      <td className="py-2 pr-4">{sp.function}</td>
                      <td className="py-2 pr-4">{sp.location}</td>
                      <td className="py-2 pr-4">{sp.legalBasis}</td>
                      <td className="py-2">{sp.dataProcessed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Separator />

          {/* Annex 3: SCCs */}
          <div className="space-y-2">
            <h2 className="text-lg font-bold">{annexes[2].title}</h2>
            <div className="text-sm whitespace-pre-line">{annexes[2].content}</div>
          </div>

          <Separator />

          {/* Annex 4: Weisungsprotokoll */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold">{annexes[3].title}</h2>
            <div className="text-sm whitespace-pre-line">{annexes[3].content}</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b">
                    {weisungsprotokollColumns.map(col => (
                      <th key={col} className="text-left py-2 pr-4 font-semibold">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 pr-4">1</td>
                    {Array(6).fill(null).map((_, i) => (
                      <td key={i} className="py-2 pr-4 text-muted-foreground italic">—</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <Separator />

          {/* Signatures */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 pt-8">
            <div className="space-y-4">
              <p className="text-sm">Ort, Datum</p>
              <div className="border-b border-foreground w-full h-8" />
              <p className="text-sm font-medium">{avv.controller.name}</p>
              <p className="text-xs text-muted-foreground">(Auftraggeber)</p>
            </div>
            <div className="space-y-4">
              <p className="text-sm">Ort, Datum</p>
              <div className="border-b border-foreground w-full h-8" />
              <p className="text-sm font-medium">{processor.name}</p>
              <p className="text-xs text-muted-foreground">(Auftragnehmer)</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AVVPreview;
